import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Logo } from '@/components/layout/Logo';
import { useAuth } from '@/auth/AuthProvider';
import { AccessLoadError, useAccess } from '@/features/AccessProvider';
import { extractErrorMessage } from '@/api/client';
import { FullScreenLoader } from '@/components/ui/Loader';

export function LoginPage() {
  const { ready, isAuthenticated, user, login } = useAuth();
  const access = useAccess();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!ready) return <FullScreenLoader label="Checking your session" />;
  if (isAuthenticated) {
    if (!access.isReady) return <FullScreenLoader label="Loading access" />;
    if (access.isError && !user?.is_superuser) return <AccessLoadError />;
    if (!access.hasAnyViewableResource) return <Navigate to="/forbidden" replace />;
    const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Sign in failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-[1fr_minmax(420px,520px)]">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden bg-primary text-primary-foreground lg:block">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_30%_20%,white,transparent_55%)]" />
        <div className="relative flex h-full flex-col justify-between p-10">
          <Logo />
          <div>
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
              The pulse of Fasto,<br />on one screen.
            </h1>
            <p className="mt-4 max-w-md text-sm text-primary-foreground/85">
              Live operations, daily close, growth, supply and strategic intelligence — built for the
              senior management and operations teams.
            </p>
          </div>
          <p className="text-xs text-primary-foreground/70">
            Asia/Kathmandu · Internal use only
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6">
          <div className="lg:hidden">
            <Logo />
          </div>
          <div className="space-y-1">
            <h2 className="font-display text-2xl font-bold tracking-tight">Sign in to Fasto BI</h2>
            <p className="text-sm text-muted-foreground">Use your staff or admin credentials.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. ops.lead"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Sign in
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            Lost access? Contact the Fasto admin team.
          </p>
        </form>
      </div>
    </div>
  );
}
